import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { type AuthAccountType, getAuthPath } from "@/lib/auth-roles";
import { SignUpForm } from "./components/form";

type SignUpProps = {
  accountType?: AuthAccountType;
};

export default function SignUp({ accountType = "restaurant" }: SignUpProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");
  const isConsumer = accountType === "consumer";
  const title = isConsumer
    ? t("auth.consumer.signUp.title", "Crear cuenta de consumidor")
    : t("auth.restaurant.signUp.title", "Crear cuenta de restaurante");
  const description = isConsumer
    ? t("auth.consumer.signUp.description", "Registrate para pedir delivery y guardar domicilios")
    : t("auth.restaurant.signUp.description", "Registrate para gestionar tu restaurante");
  const signInPath = getAuthPath(accountType, "sign-in", next);

  return (
    <>
      <div className="space-y-1 text-center">
        <h1 className="font-semibold text-2xl tracking-tight">{title}</h1>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>

      <Card className="w-full">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <SignUpForm accountType={accountType} />
        </CardContent>
        <CardFooter className="flex flex-wrap items-center justify-center gap-2">
          <div className="text-muted-foreground text-sm">
            <span className="mr-1">{t("auth.signUp.hasAccount")}</span>
            <Button aria-label="Sign in" asChild className="h-auto p-0 text-primary" variant="link">
              <Link to={signInPath}>{t("auth.signUp.signIn")}</Link>
            </Button>
          </div>
        </CardFooter>
      </Card>
    </>
  );
}
